import React from 'react';
import {
  Box, Button, Checkbox, Chip, Divider, FormControlLabel, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import JsonEditor from '../../components/JsonEditor';
import { ConfirmDialog, EditDialog } from './EditDialog';
import ScriptPicker, { parameterTemplate } from './ScriptPicker';
import { useEditor } from './useEditor';
import { ACTIONS, GLOBAL, actionSummary, definitionBody, definitionProblem, definitionToForm, emptyDefinition } from './policyEdit';

const list = (v) => (Array.isArray(v) ? v : []);

// How to recognise each program on a machine and which scripts update, install and remove it. A program defined for the whole
// company (global) can be overridden for one account by defining the same key for that account.
function ProgramsTab({ account, data, scripts, canEdit, onSave, onDelete }) {
  const editor = useEditor({ onSave, onDelete });
  const definitions = [...list(data?.definitions)].sort((a, b) => String(a.key).localeCompare(String(b.key)));
  const form = editor.editing?.form;
  const setAction = (name, patch) => editor.replace({ ...form, actions: { ...form.actions, [name]: { ...form.actions[name], ...patch } } });
  // Choosing a published script fills its mandatory parameters into the box when it is still empty. The check and the change are the
  // same parameters with DryRun true and false, which the API adds.
  const chooseScript = (name, path, script) => {
    const a = form.actions[name];
    const patch = { script: path };
    if (script) {
      if (!a.splat.trim()) patch.splat = parameterTemplate(script);
    }
    setAction(name, patch);
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">
          A program needs a definition before a rule can name it. Where an action has no script, the page can say what is wrong but nothing can fix it yet.
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" disabled={!canEdit} onClick={() => editor.open(emptyDefinition())}>Add program</Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Programs">
          <TableHead>
            <TableRow>{['Program', 'Installed name matches', 'winget id', 'Update', 'Remove', 'Defined for', ''].map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {definitions.map((d) => (
              <TableRow key={d.id || d.key} hover>
                <TableCell>{d.displayName || d.key}<Typography variant="caption" color="text.secondary" display="block">{d.key}</Typography></TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{d.detect?.namePattern}</TableCell>
                <TableCell>{d.detect?.wingetId || '-'}</TableCell>
                {ACTIONS.map(({ name }) => <TableCell key={name}>{actionSummary(d.actions?.[name], d, name)}</TableCell>)}
                <TableCell>{d.account === GLOBAL ? <Chip size="small" label="Everyone" /> : <Chip size="small" color="info" variant="outlined" label="This account" />}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Edit"><span><IconButton size="small" aria-label={`Edit program ${d.key}`} disabled={!canEdit} onClick={() => editor.open(definitionToForm(d), d)}><EditIcon fontSize="inherit" /></IconButton></span></Tooltip>
                  <Tooltip title="Delete"><span><IconButton size="small" aria-label={`Delete program ${d.key}`} disabled={!canEdit}
                    onClick={() => editor.askDelete(d.id, `Delete the definition of ${d.key}${d.account === GLOBAL ? ' for everyone' : ''}? Rules that name it stop working. The change is kept in the history.`, d.account)}><DeleteOutlineIcon fontSize="inherit" /></IconButton></span></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <EditDialog
        open={Boolean(form)} title={editor.editing?.target ? 'Edit program' : 'Add program'} onClose={editor.close} saving={editor.saving} error={editor.error}
        problem={form ? definitionProblem(form) : ''}
        onSave={() => editor.save((f) => definitionBody(account, f))}
      >
        {form && (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Key (its winget id)" value={form.key} onChange={(e) => editor.change({ key: e.target.value })} disabled={Boolean(editor.editing.target)} fullWidth />
              <TextField label="Name" value={form.displayName} onChange={(e) => editor.change({ displayName: e.target.value })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Installed name matches (pattern)" value={form.namePattern} onChange={(e) => editor.change({ namePattern: e.target.value })}
                helperText="A regular expression, for example ^Git$" inputProps={{ style: { fontFamily: 'monospace' } }} fullWidth />
              <TextField label="winget id (blank if it is not in winget)" value={form.wingetId} onChange={(e) => editor.change({ wingetId: e.target.value })} fullWidth />
            </Stack>
            <TextField label="Note" value={form.note} onChange={(e) => editor.change({ note: e.target.value })} inputProps={{ maxLength: 500 }} fullWidth multiline />
            <TextField select label="Defined for" value={form.scope} onChange={(e) => editor.change({ scope: e.target.value })} disabled={Boolean(editor.editing.target)}
              helperText="Everyone, or only this account (which then replaces the everyone definition of the same key)." fullWidth>
              <MenuItem value="global">Everyone</MenuItem>
              <MenuItem value="account">This account only</MenuItem>
            </TextField>
            {ACTIONS.map(({ name, label }) => {
              const a = form.actions[name];
              return (
                <Box key={name}>
                  <Divider sx={{ mb: 1 }} />
                  <FormControlLabel control={<Checkbox checked={a.enabled} onChange={(e) => setAction(name, { enabled: e.target.checked })} />} label={`${label}: a script exists`} />
                  {a.enabled && (
                    <Stack spacing={2} sx={{ pl: 4 }}>
                      <ScriptPicker label={`${label}: script`} value={a.script} scripts={scripts} onChange={(path, script) => chooseScript(name, path, script)} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">{label}: parameters (JSON). {'{installedName}'} stands for the exact installed name.</Typography>
                        <JsonEditor value={a.splat} label={`${label}: parameters`} minHeight={72} maxHeight={200} onChange={(text) => setAction(name, { splat: text })} />
                      </Box>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </EditDialog>

      <ConfirmDialog open={Boolean(editor.deleting)} title="Delete program" text={editor.deleting?.text} busy={editor.busy} error={editor.deleteError}
        onCancel={editor.cancelDelete} onConfirm={editor.confirmDelete} />
    </Stack>
  );
}

export default ProgramsTab;
